import { TourStep } from '../../models/tour.model';

export const NOTE_BLOCK_TOUR: TourStep[] = [
  {
    title: 'Welcome to Semeris Notes',
    intro: `
      <p>Semeris Notes is a <b>collaboration tool</b> built for analysts and legal teams.</p>
      <p>This platform empowers you to:</p>
      <ul>
        <li><b>capture</b> notes, feedback and stipulations,</li>
        <li><b>discuss</b> them collaboratively,</li>
        <li><b>revisit</b> past notes,</li>
        <li>and <b>export</b> your insights.</li>
      </ul>
      <p>As you consistently use Semeris Notes, it evolves into your <b>organizational memory</b>, facilitating deal and document review, while surfacing <b>valuable insights from previous projects</b>.</p>
      <p><i>Ready to dive in? Let's get started!</i></p>
    `,
  },
  {
    title: 'Creating and Reviewing Notes',
    intro: `
      <p>Creating and reviewing notes is straightforward. You'll primarily encounter two key icons:</p>
      <ul>
        <li><i class="material-icons">add_comment</i> - for creating a new note.</li>
        <li><i class="material-icons" style="color: var(--semeris-blue);">mode_comment</i> - to review related notes.</li>
      </ul>
      <p>These icons are strategically located throughout the application in areas such as <b>deals</b>, document <b>headings</b>, <b>paragraphs</b>, and <b>datapoint summaries</b>.</p>
    `,
  },
  {
    title: 'The anatomy of a note',
    element: 'semeris-conversation-activity-item',
    intro: `
        <p>This is an <b>example note</b>, showcasing related discussions of a hypothetical team.</p>
        <p>With Semeris Docs, the team found answers in Market Compare and in notes from previous deals.</p>
        <p><i>Let's explore the different components of this note.</i></p>
    `,
  },
  {
    title: 'The original note',
    element: 'semeris-conversation-activity-item .first-comment',
    intro: `
        <p>This displays the <b>original note</b> you've recorded, whether it's feedback, a note, or a stipulation.</p>
        <p>When creating a new note:</p>
        <ul>
          <li>Click the <i class="material-icons">send</i> icon to send/save, or</li>
          <li>Press <b>Ctrl-Enter</b>.</li>
        </ul>
        <p>To simply add a new line in your text, press <b>Enter</b>.</p>
        `,
  },
  {
    title: 'Anchors',
    element: 'semeris-conversation-activity-item semeris-conversation-anchors',
    intro: `
      <p>Anchors <i class="material-icons" style="color: var(--grey-icon-color);">link</i> indicate the note's relation to items like <b>deals</b>, <b>documents</b>, <b>headings</b>, and <b>defined terms</b>.</p>
      <p>In some cases you can even click an anchor to navigate to the referenced location in the system.</p>
    `,
  },
  {
    title: 'Snippet snapshot',
    element: 'semeris-conversation-activity-item semeris-conversation-context',
    intro: `
      <p>This area displays an <b>snippet</b> <i class="material-icons" style="color: var(--grey-icon-color);">find_in_page</i> 
      — a snapshot of content initially seen when creating the note. It might be a paragraph, a blackline, a summary in a datapoint, or other <b>relevant context</b>.</p>
      <p>The <b>snapshot</b> ensures consistency by capturing what was viewed at that moment, preventing any future confusion as content may change or evolve.</p>
    `,
  },
  {
    title: 'Assignee',
    element: 'semeris-conversation-activity-item div#notes-block-assignees',
    intro: `
      <p>The <b>Assignees</b> section, marked by the <i class="material-icons" style="color: var(--grey-icon-color);">person_pin</i> icon, indicates who's responsible for the note.</p>
      <p>Assignees simplify <b>workflows</b> and set clear <b>responsibilities</b> for each note's concerns.</p>
    `,
  },
  {
    title: 'Discussion area',
    element: 'semeris-conversation-activity-item .comments',
    intro: `
      <p>This section displays the <b>discussion</b> about the note.</p>
      <p>Each entry shows who commented and captures the conversation over time.</p>
    `,
  },
  {
    title: 'Notes remain in the original context',
    element: 'semeris-toolbar .notes-button',
    intro: `
      <p>This button on the toolbar opens the <b>Related Notes sidebar</b>, which always displays notes relevant to the page you're viewing.</p>
      <p>Every note created in the application remains connected, related, <b>anchored</b> to specific areas, be it a deal or a paragraph.</p>
      <p>When you see the <i class="material-icons" style="color: var(--semeris-blue);">mode_comment</i> icon with a number, it indicates the number of related notes you could view.</p>
    `,
  },
  {
    title: 'Notes visibility and permissions',
    intro: `
      <p>Semeris Notes is designed for <b>collaboration</b>.</p>
      <p>When you create a note, it's <b>visible to all colleagues</b> in your organization.</p>
      <p><i>We're working on introducing more detailed access controls in the future, including features like private notes.</i></p>
      <p>Rest assured, colleagues cannot edit or delete the notes you've created. They can <b>only reply</b> to them.</p>
      <p>If a note is deleted, it disappears from your view. However, for compliance, <b>your admins can still access these deleted notes</b>.</p>
      <p><i>Discover more in our other Notes-related tours.</i></p>
    `,
  },
];
